// Currency formatting helpers used across the redesign.
// Ported from redesign/charts.jsx.

export function fmt$(n: number, sign = false): string {
  const abs = Math.abs(n)
  const signed = sign && n > 0 ? '+' : ''
  const neg = n < 0 ? '-' : ''
  return (
    signed +
    neg +
    '$' +
    abs.toLocaleString('en', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  )
}

export function fmt$c(n: number): string {
  return (
    '$' +
    Math.abs(n).toLocaleString('en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  )
}

// Whole-dollar variant (no cents). Used for KPI tiles where the extra
// precision just adds noise and steals horizontal space.
export function fmt$whole(n: number): string {
  return (
    '$' +
    Math.abs(n).toLocaleString('en', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  )
}

// Same as fmt$c, but preserves the leading minus sign for negative amounts.
// Useful when a row needs to show '-$12.40' inline rather than rendering the
// sign with separate styling.
export function fmt$cSigned(n: number, useUnicodeMinus = true): string {
  const minus = useUnicodeMinus ? '−' : '-'
  const prefix = n < 0 ? minus : n > 0 ? '+' : ''
  return prefix + fmt$c(n)
}

// Backend serializes SQLAlchemy Date via Flask jsonify, which produces
// RFC-1123 strings (e.g. "Wed, 08 Apr 2025 00:00:00 GMT"). Some endpoints
// return plain "YYYY-MM-DD" instead. Normalise both to local-midnight so
// day-level comparisons are timezone-stable.
export function parseDate(s: string | null | undefined): Date {
  if (!s) return new Date(1970, 0, 1)
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
  const d = new Date(s)
  if (isNaN(d.getTime())) return new Date(1970, 0, 1)
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// Server timestamps (LastSynced, LastBalanceUpdate, ...) are written as UTC
// but stored in tz-naive DATETIME2 columns, so `.isoformat()` usually emits no
// offset ("2026-08-05T14:03:11.482"). `new Date(s)` would then read that as
// LOCAL time and report the event hours more recent than it was. Append 'Z'
// when the string carries no designator; leave already-qualified strings
// ("...Z", "...+00:00") alone. Returns epoch ms, or null if unparseable.
export function parseServerTimestamp(s: string | null | undefined): number | null {
  if (!s) return null
  const qualified = /(?:Z|[+-]\d{2}:?\d{2})$/.test(s)
  const t = new Date(qualified ? s : `${s}Z`).getTime()
  return Number.isNaN(t) ? null : t
}

export function toIsoDate(s: string | null | undefined): string {
  const d = parseDate(s)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Upstream account names arrive ALL-CAPS ("SHARE ACCOUNT", "REWARDS
// CHECKING ACCT"). Title-case for display; keep the raw string for
// tooltips/detail views. Words that are already mixed-case (e.g. "eChecking")
// are left alone, as are short all-caps tokens that read as acronyms (APY,
// HSA, IRA...).
export function titleCaseAccountName(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw
    .split(/\s+/)
    .map((w) => {
      if (!/^[A-Z0-9&.\-']+$/.test(w)) return w // already mixed case
      if (w.length <= 3 && /^[A-Z]+$/.test(w)) return w // acronym-ish
      return w.charAt(0) + w.slice(1).toLowerCase()
    })
    .join(' ')
}

// "{institution} · {account}" display label that drops the institution when
// the account name already starts with it ("American Express American
// Express Gold Card" → "American Express Gold Card").
export function accountDisplayName(
  institution: string | null | undefined,
  accountName: string | null | undefined,
): string {
  const name = titleCaseAccountName(accountName)
  const inst = (institution || '').trim()
  if (!inst) return name
  if (!name) return inst
  if (name.toLowerCase().startsWith(inst.toLowerCase())) return name
  return `${inst} ${name}`
}

export function fmtPct(n: number, fractionDigits = 0): string {
  return (
    (n * 100).toLocaleString('en', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + '%'
  )
}
